/**
 * @format
 */
import { AfterViewInit, Directive, ElementRef, Inject, Input, OnDestroy, OnInit } from "@angular/core";
import { PreviewEditTemplateSelectors } from "../preview-edit-template/state/preview-edit-template.selectors";
import { Subscription } from "rxjs";
import { UploadImagesComponent } from "./components/upload-images.component";
import { BsModalService } from "ngx-bootstrap/modal";
import { ImageOuterHTMLType, UploadImageInitialState } from "./types";
import { AuthenticationSelectors } from "../auth/state/authentication.selectors";
import { TemplateImagesService } from "./services/template-images.service";
import { PreviewEditTemplateActions } from "../preview-edit-template/state/preview-edit-template.actions";
import { Store } from "@ngrx/store";

@Directive({
   selector: "[uploadImage]",
})
export class UploadImageDirective implements OnInit, OnDestroy, AfterViewInit {
   @Input() uploadImageDisable: boolean;

   subscription: Subscription;
   buttonElementList: Array<HTMLElement>;
   inputElementList: Array<HTMLElement>;
   imageContainerList: Array<HTMLDivElement>;
   inputFile: HTMLInputElement;
   defaultWith: string = "600";
   defaultHeight: string = "300";
   isAdmin: boolean = false;
   currentUserId: number;

   styles = (width: string, id: string): string => {
      return `
    <style>
      .image__container__${id}{
        position: relative;
        width:${width};
        height:100%;
      }
      button.icon__container {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height:100%;
        font-size:40px;
        text-align:center;
        cursor: pointer;
        z-index:1;
        background-color: transparent;
        border: none;
      }
      button.icon__container:hover {
        background-color: rgba(0, 0, 0, 0.2);
      }
      button.icon__container .glyphicon {
        visibility: hidden;
      }
      button.icon__container:hover .glyphicon {
        visibility: visible;
      }

      .tooltiptext {
        position:relative; /* making the .tooltip span a container for the tooltip text */
      }

      .tooltiptext:before {
        content: attr(data-text);
        position:absolute;

        /* vertically center */
        top:0px;
        transform:translateY(-100%);

        /* move to left */
        left:-140px;

        /* basic styles */
        width:240px;
        padding:10px;
        border-radius:10px;
        background:#000;
        color: #fff;
        text-align:center;
        font-family: "Open Sans", "Helvetica Neue", sans-serif;
        font-size: 1.2rem;
        font-weight: 600;
        display:none; /* hide by default */
      }

       button.icon__container:hover span .tooltiptext:before {
         display:block;
      }


    </style>
  `;
   };

   private _buttonElementIdList: Array<string>;
   private _inputElementIdList: Array<string>;

   constructor(
      private elementRef: ElementRef<HTMLElement>,
      private previewEditSelector: PreviewEditTemplateSelectors,
      private modalService: BsModalService,
      private authenticationSelectors: AuthenticationSelectors,
      private templateImageService: TemplateImagesService,
      private store: Store,
      @Inject("IMAGE_BASE_URL") private IMAGE_BASE_URL: string,
      @Inject("MAX_FILE_SIZE") private MAX_FILE_SIZE: number
   ) {
      this.subscription = new Subscription();
      this.subscribeIsAdminSelector();
      this.subscribeAuthenticateUserId();
   }

   ngOnInit(): void {
      if (this.uploadImageDisable) {
         return;
      }
      this._buttonElementIdList = [];
      this.buttonElementList = [];
      this._inputElementIdList = [];
      this.inputElementList = [];
      this.imageContainerList = [];
   }

   ngAfterViewInit(): void {
      if (this.uploadImageDisable) {
         return;
      }

      Promise.all(this.addButtonIntoImgHtml()).then((res) => {
         this.addEventListenerToButtonBackdrop();
         this.addEventListenerToDragAndDrop();
         this.addEventListenerToInputFile();
      });
   }

   ngOnDestroy(): void {
      this.subscription.unsubscribe();

      if (Boolean(this.buttonElementList)) {
         this.buttonElementList.forEach((buttonElement) => {
            buttonElement?.removeEventListener("click", (e) => {});
         });
      }

      if (Boolean(this.inputElementList)) {
         this.inputElementList.forEach((inputElement) => {
            inputElement?.removeEventListener("change", (e) => {});
         });
      }
   }

   /**
    *  --------------------
    *  INITIALIZING SECTION
    *  --------------------
    */
   private addButtonIntoImgHtml(): Array<Promise<void>> {
      const imgRefElements = this.templateImageService.getImagesFilterByAttributeFromElement(
         this.elementRef.nativeElement,
         "ng-model"
      );

      return imgRefElements.map((htmlImageElement, index) => {
         return this.templateImageService
            .getImageSize(htmlImageElement)
            .then((imageSize) => {
               // creating object used to create new image and surrounded elements
               const createImageOuterHTML: ImageOuterHTMLType = {
                  buttonId: `upload-img-button-${index}`,
                  inputId: `input-file-${index}`,
                  imageContainerClass: `image__container__${index}`,
                  stylesCallback: () => {
                     return this.styles(imageSize.getWidth(), index.toString());
                  },
                  imageElementAsString: htmlImageElement.outerHTML,
               };
               // saving reference id to addEventListener later
               this._buttonElementIdList.push(createImageOuterHTML.buttonId);
               this._inputElementIdList.push(createImageOuterHTML.inputId);
               // change current image to new image with upload functionality
               htmlImageElement.outerHTML = this.templateImageService.getNewImageOuterHTML(createImageOuterHTML);
            })
            .catch((error) => {
               const defaultWidth = `${this.templateImageService.DEFAULT_WIDTH}px`;
               const ngModelAttr = htmlImageElement.getAttribute("ng-model");
               const imgElementAsString = `<img src="/img/img-not-available.jpg" ng-model="${ngModelAttr}" width="${defaultWidth}" alt="not-image">`;
               // creating object used to create new image and surrounded elements
               const createImageOuterHTML: ImageOuterHTMLType = {
                  buttonId: `upload-img-button-${index}`,
                  inputId: `input-file-${index}`,
                  imageContainerClass: `image__container__${index}`,
                  stylesCallback: () => {
                     return this.styles(`${this.templateImageService.DEFAULT_WIDTH}px`, index.toString());
                  },
                  imageElementAsString: imgElementAsString,
               };
               // saving reference id to addEventListener later
               this._buttonElementIdList.push(createImageOuterHTML.buttonId);
               this._inputElementIdList.push(createImageOuterHTML.inputId);
               // change current image to new image with upload functionality
               htmlImageElement.outerHTML = this.templateImageService.getNewImageOuterHTML(createImageOuterHTML);
            });
      });
   }

   private subscribeIsAdminSelector(): void {
      this.subscription.add(
         this.authenticationSelectors.selectIsAdmin().subscribe((isAdminValue) => (this.isAdmin = isAdminValue))
      );
   }

   private subscribeAuthenticateUserId(): void {
      this.subscription.add(
         this.authenticationSelectors.selectCurrentUser().subscribe((user) => {
            if (user) {
               this.currentUserId = user.userId;
            }
         })
      );
   }

   /**
    *  ----------------------
    *  INPUT CHANGE SECTION
    *  ----------------------
    */
   private addEventListenerToInputFile(): void {
      this._inputElementIdList.forEach((inputElementId) => {
         const inputElement = document.getElementById(inputElementId);

         inputElement?.addEventListener("change", (event) => this.onInputFileChange(event));

         this.inputElementList.push(inputElement);
      });
   }

   private onInputFileChange(event: Event) {
      const imgElement = this.templateImageService.getImageRefFromEvent(event);
      const inputElement = this.templateImageService.getInputFromEvent(event);
      this.saveImageAndReload(imgElement, inputElement.files);
   }

   /**
    *  ----------------------
    *  BUTTON CLICK SECTION
    *  ----------------------
    */
   private addEventListenerToButtonBackdrop(): void {
      this._buttonElementIdList.forEach((buttonElementId) => {
         const buttonElement = document.getElementById(buttonElementId);

         buttonElement?.addEventListener("click", (event) => this.onButtonClick(event));

         this.buttonElementList.push(buttonElement);
      });
   }

   private onButtonClick(event: MouseEvent): void {
      event.stopPropagation();
      event.preventDefault();

      const imageElement = this.templateImageService.getImageRefFromEvent(event);
      const inputElement = this.templateImageService.getInputFromEvent(event);

      if (this.isAdmin) {
         this.openUploadImageModal(imageElement);
         return;
      }

      inputElement.click();
   }

   private openUploadImageModal(imageElement: HTMLImageElement): void {
      const uploadImageInitialState: Partial<UploadImageInitialState> = {
         templateVariable: this.templateImageService.getNgModelValueFromImage(imageElement),
      };
      this.modalService.show(UploadImagesComponent, {
         backdrop: "static",
         class: "modal-lg modal-translated",
         keyboard: false, // disable esc key
         initialState: uploadImageInitialState,
      });
   }

   /**
    *  ----------------------
    *  DRAG AND DROP  SECTION
    *  ----------------------
    */
   private addEventListenerToDragAndDrop(): void {
      this._buttonElementIdList.forEach((buttonElementId) => {
         const imageContainer = document.getElementById(buttonElementId).parentElement as HTMLDivElement;
         // console.log("Image container found", imageContainer);
         imageContainer?.addEventListener("drop", (e) => this.onDrop(e));
         imageContainer?.addEventListener("dragover", (e) => this.onDragOver(e));
         this.imageContainerList.push(imageContainer);
      });
   }

   private onDrop(event: DragEvent) {
      event.stopPropagation();
      event.preventDefault();
      if (event.dataTransfer.items[0].type.indexOf('images/') !== -1) {
          const imgElement = this.templateImageService.getImageRefFromEvent(event);
          if (this.isAdmin) {
              this.openUploadImageModalForDrop(event);
              return;
          }
          this.saveImageAndReload(imgElement, event.dataTransfer.files);
      }
   }

   private openUploadImageModalForDrop(event: DragEvent) {
      const imgElement = this.templateImageService.getImageRefFromEvent(event);

      const uploadImageInitialState: Partial<UploadImageInitialState> = {
         templateVariable: this.templateImageService.getNgModelValueFromImage(imgElement),
         droppedFiles: event.dataTransfer.files,
      };

      this.modalService.show(UploadImagesComponent, {
         backdrop: "static",
         class: "modal-lg modal-translated",
         keyboard: false, // disable esc key
         initialState: uploadImageInitialState,
      });
   }

   private onDragOver(e: Event) {
      e.preventDefault();
   }

   /**
    *  ---------------
    *  SHARING METHODS
    *  --------------
    */
   private saveImageAndReload(imgElement: HTMLImageElement, files: FileList): void {
      console.log("----- SAVING IMAGE IN CURRENT LOGGED-IN USER FOLDER & RELOAD ----");
      const fileToUpload = files.item(0);

      if (fileToUpload.size > this.MAX_FILE_SIZE) {
         this.templateImageService.openAlertMaxSizeExceed(fileToUpload.name);
         return;
      }

      const templateVariable = this.templateImageService.getNgModelValueFromImage(imgElement);
      this.subscription.add(
         this.templateImageService.uploadImage(fileToUpload, fileToUpload.name).subscribe((result) =>
            this.store.dispatch(
               PreviewEditTemplateActions.useImageUploaded({
                  imagesList: [
                     {
                        fileName: `${this.currentUserId}/${fileToUpload.name}`,
                        tmpFileName: fileToUpload.name,
                        variableName: templateVariable,
                        variableValue: "",
                     },
                  ],
                  imageUrl: `${this.IMAGE_BASE_URL}${this.currentUserId}/${fileToUpload.name}`,
                  variableName: templateVariable,
                  reload: true,
               })
            )
         )
      );
   }
}
